/**
 * Pure helpers behind the AI Workspace settings screen.
 *
 * Everything here is arithmetic or wording with no Vue, no axios and no component state,
 * so the rules that are easy to get quietly wrong — either-off-means-off, micros
 * conversion, sending only what changed — are testable on their own. `settings.spec.js`
 * covers them.
 */

/** Millionths of a currency unit, the resolution `org_spend_ceiling_micros` is stored in. */
export const MICROS_PER_UNIT = 1000000;

/** ADR-0002. A larger value is rejected by the backend rather than stored. */
export const MAX_GENERATION_TIMEOUT_SECONDS = 300;

/**
 * Whether the workspace is actually accepting messages, and which switch is stopping it.
 *
 * The rule is *either one being off means off* — not "the environment variable
 * overrides the database", not the reverse. It is the detail people misremember under
 * pressure, which is exactly when they are reading this screen, so the wording names the
 * switch that is off and says plainly whether the other one can bring it back.
 *
 * @param {Object} config - { chatEnabled, chatEnabledByEnvironment }
 * @returns {Object} { on, blockedBy, headline, detail }
 */
export function describeAvailability(config) {
  const database = Boolean(config && config.chatEnabled);
  const environment = Boolean(config && config.chatEnabledByEnvironment);

  if (database && environment) {
    return {
      on: true,
      blockedBy: null,
      headline: "The AI workspace is on",
      detail: "The workspace is accepting messages.",
    };
  }

  // Wording deliberately free of variable and file names: the person reading this is
  // deciding who to call, not editing configuration. What they need is whether the
  // switch in front of them is the remedy.
  if (!database && !environment) {
    return {
      on: false,
      blockedBy: "both",
      headline: "The AI workspace is off",
      detail:
        "The workspace is switched off here and separately blocked by engineering. " +
        "Switching it back on will not restore it until engineering lifts that block.",
    };
  }

  if (!environment) {
    return {
      on: false,
      blockedBy: "environment",
      headline: "The AI workspace is off",
      detail:
        "Engineering has the workspace blocked. The switch here cannot lift it — an " +
        "engineer has to, and until they do nobody can send a message.",
    };
  }

  return {
    on: false,
    blockedBy: "database",
    headline: "The AI workspace is off",
    detail: "Switched off here. Turning the switch back on restores it for everyone.",
  };
}

/**
 * The fields that actually changed, as the PATCH body.
 *
 * The screen sends this rather than the whole form on purpose. A form that round-trips
 * every field would revert a token budget someone else raised an hour ago, purely
 * because it was loaded before their change — and an administrator who came here only to
 * flip the kill switch would never know they had done it.
 *
 * `undefined` means "not being changed" and is dropped; `null` is a real value and is
 * kept, which is how a spend ceiling gets removed.
 *
 * @param {Object} original - the values as loaded
 * @param {Object} edited - the values as they stand now
 * @returns {Object} only the differing fields
 */
export function changedFields(original, edited) {
  const changes = {};

  Object.keys(edited || {}).forEach((key) => {
    const next = edited[key];
    if (next === undefined) {
      return;
    }
    if (!isSameValue(original ? original[key] : undefined, next)) {
      changes[key] = next;
    }
  });

  return changes;
}

function isSameValue(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
}

/**
 * Micros to whole currency units, for display and for the input.
 *
 * @param {Number|null} micros
 * @returns {Number|null}
 */
export function microsToUnits(micros) {
  if (micros === null || micros === undefined || micros === "") {
    return null;
  }
  return Number(micros) / MICROS_PER_UNIT;
}

/**
 * Units back to micros, rounded rather than truncated.
 *
 * `2.10 * 1e6` is 2099999.9999999995 in binary floating point, and truncating would
 * store a ceiling a hair under what was typed. Rounding keeps the stored value the one
 * the administrator entered.
 *
 * @param {Number|String|null} units
 * @returns {Number|null}
 */
export function unitsToMicros(units) {
  if (units === null || units === undefined || units === "") {
    return null;
  }
  return Math.round(Number(units) * MICROS_PER_UNIT);
}

/**
 * Alert thresholds from what someone typed.
 *
 * Accepts percentages or fractions in one box — "50, 80, 95" and "0.5, 0.8, 0.95" mean
 * the same thing, because both are what people write and the backend takes fractions.
 * Anything above 1 is read as a percentage.
 *
 * @param {String} text
 * @returns {Number[]|null} fractions in 0..1, or null when the text is unusable
 */
export function parseThresholds(text) {
  if (text === null || text === undefined || String(text).trim() === "") {
    return [];
  }

  const parts = String(text)
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "");

  const values = [];
  for (const part of parts) {
    const raw = Number(part.replace("%", ""));
    if (!Number.isFinite(raw) || raw <= 0) {
      return null;
    }
    const fraction = raw > 1 ? raw / 100 : raw;
    if (fraction > 1) {
      return null;
    }
    values.push(Math.round(fraction * 10000) / 10000);
  }

  return values.sort((a, b) => a - b);
}

/**
 * Fractions back to the percentages people read.
 *
 * @param {Number[]|null} thresholds
 * @returns {String}
 */
export function formatThresholds(thresholds) {
  if (!Array.isArray(thresholds) || !thresholds.length) {
    return "";
  }
  return thresholds.map((value) => `${Math.round(value * 1000) / 10}`).join(", ");
}

/**
 * The roles nobody has put in the pilot yet.
 *
 * Denial by omission is the quiet failure this whole screen exists to make loud: the
 * role has the permission, the user sees the workspace, and every send is refused.
 *
 * @param {Array} assignments - [{ roleId, roleName, quotaProfileId }]
 * @returns {Array} the ones carrying no profile
 */
export function rolesWithoutProfile(assignments) {
  return (assignments || []).filter((row) => !row.quotaProfileId);
}

/**
 * Thousands separators, for numbers that are otherwise a wall of digits.
 *
 * @param {Number|null} value
 * @returns {String}
 */
export function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  return Number(value).toLocaleString("en-GB");
}

/**
 * A spend figure in currency units, from micros.
 *
 * @param {Number|null} micros
 * @returns {String}
 */
export function formatMicros(micros) {
  if (micros === null || micros === undefined) {
    return "—";
  }
  return (Number(micros) / MICROS_PER_UNIT).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
